import { ApplicationConfig, ErrorHandler, LOCALE_ID, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { APP_BASE_HREF, CurrencyPipe, DatePipe, DecimalPipe, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { provideRouter, withRouterConfig } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { BrowserModule, Title } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { switchMap } from 'rxjs';
import { provideToastr } from 'ngx-toastr';
import { AllowUnsafeHtmlInSlider } from '@angular-slider/ngx-slider';
import { FormlyModule } from '@ngx-formly/core';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { FormlyBootstrapModule } from '@ngx-formly/bootstrap';
import { ModalModule } from 'ngx-bootstrap/modal';
import { KeyboardShortcutsModule } from 'ng-keyboard-shortcuts';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import fuzziersort from 'fuzziersort';
import { headerAcceptInterceptor } from './interceptors/header-accept.interceptor';
import { headerContentTypeInterceptor } from './interceptors/header-content-type.interceptor';
import { headerAuthorizationInterceptor } from './interceptors/header-authorization.interceptor';
import { httpErrorInterceptor } from './interceptors/http-error.interceptor';
import { cursorMetaInterceptor } from './interceptors/cursor-meta.interceptor';

import { ApplicationInitService } from './services/application-init.service';
import { RegionService, EnvService, FormlyValidationTranslationService, GlobalErrorHandler, Logger, ConsoleLoggerService } from '@parashift/shared/services';
import { routes } from './app.routes';
import { formConfig } from '@parashift/shared/features';
import { PageNumberPipe, PdcDatePipe } from '@parashift/shared/pipes';
import { LocalRegionService } from './services/local-region.service';
import { LocalEnvService } from './services/local-env.service';
import { environment } from '../environments/environment';
import { ENVIRONMENT_CONFIG } from '@parashift/shared/environment-config';

export function appInit(envService: EnvService, applicationInitService: ApplicationInitService) {
  return envService.getConfig().pipe(
    switchMap(() => applicationInitService.handleAuthentication())
  );
}

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      BrowserModule,
      FormlyModule.forRoot({
        ...formConfig,
        extras: {
          lazyRender: false,
          resetFieldOnHide: false,
          checkExpressionOn: 'changeDetectionCheck',
          immutable: false,
        }
      }),
      BsDropdownModule.forRoot(),
      BsDatepickerModule.forRoot(),
      FormsModule,
      ReactiveFormsModule,
      FormlyBootstrapModule,
      ModalModule.forRoot(),
      KeyboardShortcutsModule.forRoot(),
      FontAwesomeModule
    ),
    provideRouter(routes, withRouterConfig({ paramsInheritanceStrategy: 'always' })),
    provideHttpClient(withInterceptors([
      headerAcceptInterceptor,
      headerContentTypeInterceptor,
      headerAuthorizationInterceptor,
      httpErrorInterceptor,
      cursorMetaInterceptor
    ])),
    provideAnimations(),
    provideToastr({ enableHtml: true }),
    ApplicationInitService,
    Title,
    CurrencyPipe,
    DecimalPipe,
    DatePipe,
    PageNumberPipe,
    PdcDatePipe,
    { provide: APP_BASE_HREF, useValue: '/' },
    { provide: LocationStrategy, useClass: PathLocationStrategy },
    { provide: RegionService, useClass: LocalRegionService },
    { provide: ENVIRONMENT_CONFIG, useValue: environment },
    { provide: LOCALE_ID, useValue: 'de-CH' },
    { provide: EnvService, useClass: LocalEnvService },
    { provide: 'fuzziersort', useFactory: () => fuzziersort },
    provideAppInitializer(() => appInit(inject(EnvService), inject(ApplicationInitService))),
    provideAppInitializer(() => inject(FormlyValidationTranslationService).init()),
    { provide: AllowUnsafeHtmlInSlider, useValue: false },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: Logger, useClass: ConsoleLoggerService },
  ]
};
